// Small display formatters shared across pages.

const DATE_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

const DATE_FMT_UTC = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
})

/**
 * "Mar 14, 2026" for a date string; "—" when absent. UTC-midnight timestamps
 * (date-only values like posted_date) format in UTC so they don't shift a day.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  const isDateOnly = /T00:00:00(\.0+)?(Z|\+00:00)$/.test(value) || !value.includes('T')
  return (isDateOnly ? DATE_FMT_UTC : DATE_FMT).format(d)
}

/** Relative "3m ago" / "2h ago" / "5d ago" label for timestamps. */
export function timeAgo(value: string | null | undefined): string {
  if (!value) return ''
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return ''
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return formatDate(value)
}

/** ISO/RFC3339 → value for <input type="datetime-local"> (local time). */
export function toDatetimeLocal(value: string | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** <input type="datetime-local"> value → ISO string (or null when cleared). */
export function fromDatetimeLocal(value: string): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

/** "512 B" / "24.3 KB" / "1.2 MB" for a byte count; "—" when absent. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '—'
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`
  const mb = kb / 1024
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`
}

/**
 * Notice types, and what they mean for whether we can bid.
 *
 * The mark is the fast signal on a dense board: money means the government is
 * ready to receive prices, the hourglass means it isn't yet, and the glass
 * means they're only looking around. Someone scanning forty cards reads the
 * emoji; the label is there for everyone else, and for screen readers.
 */
export interface NoticeTypeMeta {
  /** Decorative — always paired with `short`, never the only signal. */
  emoji: string
  /** Fits on a card. */
  short: string
  /** Spelled out, with what it means for us. Used as the tooltip. */
  full: string
}

const NOTICE_TYPE_META: Record<string, NoticeTypeMeta> = {
  o: { emoji: '💵', short: 'Solicitation', full: 'Solicitation — open for bids' },
  k: { emoji: '💵', short: 'Combined', full: 'Combined Synopsis/Solicitation — open for bids' },
  p: { emoji: '⏳', short: 'Presol', full: 'Presolicitation — announced, not open for bids yet' },
  r: { emoji: '🔍', short: 'Sources Sought', full: 'Sources Sought — market research, nothing to price yet' },
  s: { emoji: '📌', short: 'Special', full: 'Special Notice' },
  a: { emoji: '🏆', short: 'Award', full: 'Award Notice — already awarded' },
  i: { emoji: '📦', short: 'Bundle', full: 'Intent to Bundle Requirements' },
  g: { emoji: '🏷️', short: 'Surplus', full: 'Sale of Surplus Property' },
  u: { emoji: '⚖️', short: 'J&A', full: 'Justification and Approval' },
}

/**
 * SAM speaks two dialects for the same thing: the API's ptype CODES ("o", "k")
 * and the human labels stored on the catalog row ("Combined
 * Synopsis/Solicitation"). Production stores the labels, so a map keyed only
 * on codes silently matches nothing — the same trap the Go gate hit
 * (internal/autosource/autosource.go, canonicalNoticeType).
 */
const NOTICE_TYPE_ALIASES: Record<string, string> = {
  solicitation: 'o',
  'combined synopsis/solicitation': 'k',
  'combined synopsis': 'k',
  presolicitation: 'p',
  'pre-solicitation': 'p',
  'sources sought': 'r',
  'special notice': 's',
  'award notice': 'a',
  'intent to bundle requirements (dod-funded)': 'i',
  'sale of surplus property': 'g',
  'justification and approval (j&a)': 'u',
  justification: 'u',
}

/** Unknown types keep their own name rather than being mislabelled. */
const UNKNOWN_NOTICE_TYPE: NoticeTypeMeta = { emoji: '📄', short: '', full: '' }

export function noticeTypeMeta(value: string | null | undefined): NoticeTypeMeta | null {
  if (!value) return null
  const v = value.trim().toLowerCase()
  const code = NOTICE_TYPE_META[v] ? v : NOTICE_TYPE_ALIASES[v]
  return NOTICE_TYPE_META[code] ?? { ...UNKNOWN_NOTICE_TYPE, short: value, full: value }
}

/** SAM.gov notice type → short human label (falls back to the raw value). */
export function noticeTypeLabel(value: string | null | undefined): string {
  return noticeTypeMeta(value)?.short || '—'
}

/** "45s" / "1m 8s" / "1h 4m" between two timestamps; "—" when incomplete. */
export function formatDuration(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end) return '—'
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (Number.isNaN(ms) || ms < 0) return '—'
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

const MONEY_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** "$1,234.50" for an amount; "—" when absent. */
export function formatMoney(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—'
  return MONEY_FMT.format(amount)
}

/**
 * sub_tier arrives as a dotted chain ("DEPT OF DEFENSE.DEPT OF THE AIR
 * FORCE.…"). For compact rows, return the most specific segment.
 */
export function subTierLeaf(subTier: string | null | undefined): string {
  if (!subTier) return ''
  const parts = subTier.split('.').filter(Boolean)
  return parts[parts.length - 1] ?? ''
}
